/* ══════════════════════════════════════════════════════════════════
   Was im Verlauf vor dieser Seite steht — für den Rückpfeil im Kopf.

   **Der Pfeil legte bis zum 17.09.2026 einen neuen Eintrag an.** Er ist ein
   Verweis auf die Liste (`Kopf.tsx`, `Zurueck`), und jeder Druck darauf
   schob die Liste auf den Verlauf. Die Zurückgeste auf Android führte
   danach wieder in das Detail, aus dem man gerade kam — und von dort der
   Pfeil wieder in die Liste: eine Schleife aus zwei Seiten
   (40-mobiles-grundgeruest.md, „Wo hängt noch etwas an der Adresse?", 5).

   **Jetzt fragt der Pfeil, was davor steht** (Schritt 7). Ist es die Liste,
   geht er im Verlauf zurück — mit ihren Filtern und ihrem Rollstand, so
   wie die Geste. Ist es etwas anderes (man kam aus der Planung, über einen
   Link, oder hat die Seite frisch geöffnet), **ersetzt** er das Detail
   durch die Liste. So entsteht kein Eintrag, zu dem die Geste zurückführen
   könnte.

   Der Browser verrät nicht, was vor der aktuellen Seite steht. Der Router
   schreibt aber an jeden Eintrag seine Stelle (`__TSR_index`); hier wird
   je Stelle der Pfad gemerkt, für diesen Reiter (`sessionStorage`), damit
   es auch nach einem Neuladen noch stimmt.
   ══════════════════════════════════════════════════════════════════ */

type Ort = { pathname: string; state: { __TSR_index?: number } };

/** Das, was hier vom Verlauf des Routers gebraucht wird. */
export type Verlauf = {
  location: Ort;
  subscribe: (h: (anlass: { location: Ort; action: { type: string } }) => void) => () => void;
  back: () => void;
};

const SCHLUESSEL = "mm_verlauf";

let verlauf: Verlauf | null = null;
let stellen: Record<string, string> = {};
/** Läuft gerade ein Schritt zurück, den der Pfeil ausgelöst hat? (`schliessanfrage.ts`) */
let eigenerSchritt = false;

function lesen(): Record<string, string> {
  try {
    const roh = sessionStorage.getItem(SCHLUESSEL);
    return roh ? JSON.parse(roh) as Record<string, string> : {};
  } catch {
    return {};
  }
}

function schreiben(): void {
  try {
    sessionStorage.setItem(SCHLUESSEL, JSON.stringify(stellen));
  } catch {
    /* Ohne Speicher gilt es bis zum Neuladen — dann geht der Pfeil zur Liste. */
  }
}

function merken(ort: Ort, art: string): void {
  const stelle = ort.state.__TSR_index ?? 0;
  // Ein neuer Eintrag schneidet ab, was dahinter lag: Dorthin führt kein Vor mehr.
  if (art === "PUSH") {
    for (const k of Object.keys(stellen)) if (Number(k) > stelle) delete stellen[k];
  }
  stellen[String(stelle)] = ort.pathname;
  schreiben();
}

/** Einmal beim Start (`main.tsx`): Ab hier wird mitgeschrieben. */
export function verlaufFolgen(v: Verlauf): void {
  verlauf = v;
  stellen = lesen();
  merken(v.location, "LADEN");
  v.subscribe(({ location, action }) => {
    merken(location, action.type);
    eigenerSchritt = false;
  });
}

/** Der Pfad des Eintrags vor diesem — oder `null`, wenn es keinen gibt oder er unbekannt ist. */
export function vorigerWeg(): string | null {
  const stelle = verlauf?.location.state.__TSR_index;
  if (stelle === undefined || stelle < 1) return null;
  return stellen[String(stelle - 1)] ?? null;
}

/** Einen Schritt zurück — als eigener Schritt gekennzeichnet, den kein Blatt abfängt. */
export function zurueckGehen(): void {
  if (!verlauf) return;
  eigenerSchritt = true;
  verlauf.back();
}

export function eigenerSchrittLaeuft(): boolean {
  return eigenerSchritt;
}
